import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, MenuItem, Tooltip } from "@mui/material";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import { useSelector } from "react-redux";
import CustomTableHeading from '../CommonComponents/CustomTableHeading';
import CustomTableMetric from '../CommonComponents/CustomTableMetric';
import CustomDialogBox from "../CommonComponents/CustomDialogBox";
import Dialogbox from "../CommonComponents/Dialogbox";
import LK_Add from '../../images/LK_Add.js';
import LK_Delete from "../../images/LK_Delete";
import "../../cssfiles/homepage.css";
import "../../cssfiles/auditlogconfig.css";

// Import your API service functions
import { 
    getAuditProcesses, 
    addAuditProcess, 
    deleteAuditProcess,
    getMasterAuditProcesses
} from '../../Restclient/datacollection';

export default function AuditConfig() {
    
    const themechange = useSelector((state) => state.themedata.theme);
    const userdetails = useSelector((state) => state.logindata.userdetails);
    const selectedprojectname = useSelector((state) => state.mainpagedata.selectedprojectname);
    
    // State variables
    const [configuredProcesses, setConfiguredProcesses] = useState([]);
    const [masterProcesses, setMasterProcesses] = useState([]); // New state for master processes
    const [selectedProcess, setSelectedProcess] = useState('');
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedDeleteProcess, setSelectedDeleteProcess] = useState(null);
    const [successDialog, setSuccessDialog] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorDialog, setErrorDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingMasterProcesses, setLoadingMasterProcesses] = useState(false);

    // Fetch master processes on component mount
    useEffect(() => {
        fetchMasterProcesses();
    }, []);

    // Fetch configured audit processes when project changes
    useEffect(() => {
        if (selectedprojectname && userdetails?.Userid) {
            fetchAuditProcesses();
        }
    }, [selectedprojectname]);

    // Fetch master processes from backend
    const fetchMasterProcesses = () => {
        setLoadingMasterProcesses(true);
        
        getMasterAuditProcesses((response, status) => {
            setLoadingMasterProcesses(false);
            
            if (status === 200 && response?.data?.processes) {
                // Expecting response.data.processes to be an array of process names
                setMasterProcesses(response.data.processes);
            } else {
                setErrorMessage('Failed to fetch available audit processes');
                setErrorDialog(true);
                // Fallback to empty array
                setMasterProcesses([]);
            }
        });
    };

    // Fetch configured audit processes from backend
    const fetchAuditProcesses = () => {
        setLoading(true);
        const projectId = userdetails.Projectid === 'All' ? selectedprojectname : userdetails.Projectid;
        
        getAuditProcesses((response, status) => {
            setLoading(false);
            if (status === 200 && response?.data?.processes) {
                setConfiguredProcesses(response.data.processes);
            } else if (status === 500 || status === 'error') {
                setErrorMessage('Could not fetch Audit Log Processes');
                setErrorDialog(true);
            }
        });
    };

    // Get available processes (not already configured)
    const getAvailableProcesses = () => {
        return masterProcesses.filter(p => !configuredProcesses.includes(p));
    };

    // Handle adding a new audit process
    const handleAddProcess = () => {
        if (!selectedProcess) {
            setErrorMessage('Please select a process to add');
            setErrorDialog(true);
            return;
        }

        const projectId = userdetails.Projectid === 'All' ? selectedprojectname : userdetails.Projectid;

        const newProcess = {
            projectId: projectId,
            processType: selectedProcess,
            createdBy: userdetails.Userid
        };

        addAuditProcess(newProcess, userdetails.Userid, (response, status) => {
            if (status === 200 || status === 201) {
                setSuccessMessage('Audit process added successfully');
                setSuccessDialog(true);
                setSelectedProcess('');
                fetchAuditProcesses();
            } else {
                setErrorMessage(response?.data?.message || 'Failed to add audit process');
                setErrorDialog(true);
            }
        });
    };

    // Handle delete process
    const handleDeleteClick = (processName) => {
        setSelectedDeleteProcess(processName);
        setOpenDeleteDialog(true);
    };

    const handleDeleteConfirm = () => {
        if (!selectedDeleteProcess) return;

        const projectId = userdetails.Projectid === 'All' ? selectedprojectname : userdetails.Projectid;

        deleteAuditProcess(
            selectedDeleteProcess,
            (response, status) => {
                if (status === 200) {
                    setSuccessMessage('Audit process deleted successfully');
                    setSuccessDialog(true);
                    fetchAuditProcesses();
                } else {
                    setErrorMessage(response?.data?.message || 'Failed to delete audit process');
                    setErrorDialog(true);
                }
                setOpenDeleteDialog(false);
                setSelectedDeleteProcess(null);
            }
        );
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedDeleteProcess(null);
    };

    const isDropdownDisabled = loading || loadingMasterProcesses || getAvailableProcesses().length === 0;

    // Get tooltip message for Add button
    const getAddButtonTooltip = () => {
        if (loading || loadingMasterProcesses) {
            return 'Loading...';
        }
        if (masterProcesses.length === 0) {
            return 'No processes available from master list';
        }
        if (getAvailableProcesses().length === 0) {
            return 'All available processes have been configured';
        }
        if (!selectedProcess) {
            return 'Select a process from the dropdown to add';
        }
        return 'Add selected process';
    };

    // Get helper text for dropdown
    const getDropdownHelperText = () => {
        if (loadingMasterProcesses) {
            return 'Loading available processes...';
        }
        if (masterProcesses.length === 0) {
            return 'No processes available in master list';
        }
        if (getAvailableProcesses().length === 0) {
            return 'All available processes have been configured';
        }
        return 'Select a process to enable audit logging';
    };

    return (
        <Box className="audit-config-container">
            <Box className="audit-config-header">
                <Typography className="audit-config-title">
                    Audit Log Configuration
                </Typography>
                <Typography className="audit-config-subtitle">
                    Configure which processes generate audit logs for this project
                </Typography>
            </Box>

            {/* Add Process Section */}
            <Box className="audit-config-add-section">
                <TextField
                    select
                    label="Select Process"
                    value={selectedProcess}
                    onChange={(e) => setSelectedProcess(e.target.value)}
                    className={`audit-config-dropdown audit-config-dropdown-${themechange}`}
                    variant="outlined"
                    disabled={isDropdownDisabled}
                    fullWidth
                    helperText={getDropdownHelperText()}
                >
                    {getAvailableProcesses().length === 0 && !loadingMasterProcesses ? (
                        <MenuItem disabled>
                            {masterProcesses.length === 0 ? 'No processes available' : 'All processes configured'}
                        </MenuItem>
                    ) : (
                        getAvailableProcesses().map((processName) => (
                            <MenuItem key={processName} value={processName}>
                                {processName}
                            </MenuItem>
                        ))
                    )}
                </TextField>
                <Tooltip 
                    title={getAddButtonTooltip()} 
                    arrow
                    placement="top"
                >
                    <span>
                        <Button
                            onClick={handleAddProcess}
                            disabled={!selectedProcess || loading || loadingMasterProcesses}
                            className="audit-config-add-button"
                            variant="text"
                            disableTouchRipple
                        >
                            <LK_Add color={"#005C8E"} width={"45px"} height={"45px"} />
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            {/* Configured Processes Table */}
            <Box className="audit-config-table-wrapper">
                <TableContainer component={Paper} className="audit-config-table-container" elevation={0}>
                    <Table>
                        <TableHead>
                            <TableRow className="audit-config-table-header-row">
                                <TableCell align="left">
                                    <CustomTableHeading children={'Process Name'} />
                                </TableCell>
                                <TableCell align="center">
                                    <CustomTableHeading children={'Delete'} />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={2} align="center" className="audit-config-loading-cell">
                                        <Typography className="audit-config-loading-text">
                                            Loading...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : configuredProcesses.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={2} align="center" className="audit-config-empty-cell">
                                        <Typography className="audit-config-empty-text">
                                            No audit processes configured
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                configuredProcesses.map((processName, index) => (
                                    <TableRow 
                                        key={index} 
                                        className="audit-config-table-row"
                                    >
                                        <TableCell>
                                            <CustomTableMetric children={processName} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Button
                                                onClick={() => handleDeleteClick(processName)}
                                                className="audit-config-delete-button"
                                                variant="text"
                                                disableTouchRipple
                                            >
                                                <LK_Delete color={"#005C8E"} width={"25px"} height={"25px"} />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Dialog boxes */}
            <Dialogbox
                dialogtitle={"Confirm Deletion"}
                dialogtext={`Are you sure you want to delete the audit process "${selectedDeleteProcess}"?`}
                openDialog={openDeleteDialog}
                handleCloseDialog={handleCloseDeleteDialog}
                handleConfirmDialog={handleDeleteConfirm}
                themechange={themechange}
                confirmText={"Yes"}
                cancelText={"Cancel"}
            />

            <CustomDialogBox
                dialogTitle={"Success"}
                dialogText={successMessage}
                openDialog={successDialog}
                handleCloseDialog={() => setSuccessDialog(false)}
                handleConfirmDialog={() => setSuccessDialog(false)}
                confirmText={"OK"}
                type={"Success"}
            />

            <CustomDialogBox
                dialogTitle={"Error"}
                dialogText={errorMessage}
                openDialog={errorDialog}
                handleCloseDialog={() => setErrorDialog(false)}
                handleConfirmDialog={() => setErrorDialog(false)}
                confirmText={"OK"}
                type={"Error"}
            />
        </Box>
    );
}